<template>
  <div 
    class="reactive-card"
    @mousemove="handleMouseMove"
    @mouseenter="isHovering = true"
    @mouseleave="handleMouseLeave"
    ref="cardRef"
    :style="cardTransform"
  >
    <div class="card-glow" :style="glowPosition"></div>
    <div class="card-content">
      <slot></slot>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';

const cardRef = ref(null);
const isHovering = ref(false);
const mouseX = ref(0.5);
const mouseY = ref(0.5);

const handleMouseMove = (e) => {
  if (!cardRef.value) return;
  const rect = cardRef.value.getBoundingClientRect();
  mouseX.value = (e.clientX - rect.left) / rect.width;
  mouseY.value = (e.clientY - rect.top) / rect.height;
};

const handleMouseLeave = () => {
  isHovering.value = false;
  // Don't reset position - let it fade out where it is
};

// Subtle 3D tilt effect based on mouse position
const cardTransform = computed(() => {
  if (!isHovering.value) return {};
  
  const tiltX = (mouseY.value - 0.5) * -10; // Max 5deg tilt
  const tiltY = (mouseX.value - 0.5) * 10;
  
  return {
    transform: `perspective(1000px) rotateX(${tiltX}deg) rotateY(${tiltY}deg) scale(1.02)`,
  };
});

// Gradient glow that follows mouse
const glowPosition = computed(() => ({
  background: `radial-gradient(circle at ${mouseX.value * 100}% ${mouseY.value * 100}%, rgba(255, 165, 0, 0.3), transparent 60%)`,
  opacity: isHovering.value ? 1 : 0,
}));
</script>

<style scoped>
.reactive-card {
  position: relative;
  padding: 2rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05));
  border: 1px solid rgba(255, 165, 0, 0.3);
  border-radius: 16px;
  backdrop-filter: blur(10px);
  box-shadow: 
    0 4px 6px rgba(0, 0, 0, 0.1),
    0 0 20px rgba(255, 165, 0, 0.1);
  transition: all 0.6s cubic-bezier(0.25, 0, 0.25, 1);
  overflow: hidden;
  transform-style: preserve-3d;
}

.reactive-card:hover {
  border-color: rgba(255, 165, 0, 0.5);
  box-shadow: 
    0 8px 16px rgba(0, 0, 0, 0.2),
    0 0 40px rgba(255, 165, 0, 0.2);
}

.card-glow {
  position: absolute;
  inset: 0;
  pointer-events: none;
  transition: opacity 1.5s ease, background 2s ease;
  z-index: 0;
}

.card-content {
  position: relative;
  z-index: 1;
}
</style>
